import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type { AdminRefund, ReviewOutcome } from '@/types/sales'
import type { FeeHistoryEntry, FeeOverride, FeeRule, FeeSimulation, SimulationInput } from '@/types/adminFees'
import type { AdminPayout, AdminRecipientBalance } from '@/types/monetization'

/**
 * SuperAdmin: Memberfy's platform fee rules and the per-community overrides
 * (F-43 and the rules editor). Every route answers 403 to anyone else.
 */
export class AdminFeesResource {
  constructor(private ctx: SdkContext) {}

  /** Active rules first, then by priority. */
  async listRules(): Promise<FeeRule[]> {
    return listItems(await this.ctx.raw({ httpMethod: 'GET', path: '/admin/fee-rules' }))
  }

  /** The rule with `history`: the last 100 changes. */
  async getRule(id: string): Promise<FeeRule & { history?: FeeHistoryEntry[] }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/fee-rules/{id}' }, { id })
  }

  async createRule(body: Record<string, unknown>): Promise<FeeRule> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/fee-rules', hasBody: true }, { body })
  }

  /** Only the fields sent change; `null` clears a nullable one. */
  async updateRule(id: string, body: Record<string, unknown>): Promise<FeeRule> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/admin/fee-rules/{id}', hasBody: true }, { id, body })
  }

  async setRuleActive(id: string, active: boolean): Promise<FeeRule> {
    return this.ctx.call(
      { httpMethod: 'POST', path: active ? '/admin/fee-rules/{id}/activate' : '/admin/fee-rules/{id}/deactivate', hasBody: true },
      { id, body: {} }
    )
  }

  /** Which rule and override a sale would get, and the result; never the provider's cost. */
  /** Logical delete. 400 when it is the last general rule in force; 404 when already gone. */
  async deleteRule(id: string): Promise<{ id: string; deletedAt: string }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/admin/fee-rules/{id}' }, { id })
  }

  async simulate(input: SimulationInput): Promise<FeeSimulation> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/fee-rules/simulate', hasBody: true }, { body: input })
  }

  /** `{ overrides, effectiveInstallmentRates }` for one community. */
  async communityOverrides(communityId: string): Promise<{ overrides: FeeOverride[]; effectiveInstallmentRates: Record<string, string> }> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/admin/communities/{communityId}/fee-overrides' }, { communityId })
  }

  async getOverride(communityId: string, id: string): Promise<FeeOverride & { history?: FeeHistoryEntry[] }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}/fee-overrides/{id}' }, { communityId, id })
  }

  async createOverride(communityId: string, body: Record<string, unknown>): Promise<FeeOverride> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/admin/communities/{communityId}/fee-overrides', hasBody: true },
      { communityId, body }
    )
  }

  async updateOverride(communityId: string, id: string, body: Record<string, unknown>): Promise<FeeOverride> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/admin/communities/{communityId}/fee-overrides/{id}', hasBody: true },
      { communityId, id, body }
    )
  }

  async setOverrideActive(communityId: string, id: string, active: boolean): Promise<FeeOverride> {
    return this.ctx.call(
      {
        httpMethod: 'POST',
        path: active
          ? '/admin/communities/{communityId}/fee-overrides/{id}/activate'
          : '/admin/communities/{communityId}/fee-overrides/{id}/deactivate',
        hasBody: true
      },
      { communityId, id, body: {} }
    )
  }

  /** Logical delete; the community's sales go back to the platform rules. 404 when already gone. */
  async deleteOverride(communityId: string, id: string): Promise<{ id: string; deletedAt: string }> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/admin/communities/{communityId}/fee-overrides/{id}' },
      { communityId, id }
    )
  }

  /** The SuperAdmin's own communities: quick picks for overrides and the simulator. */
  async myCommunities(): Promise<{ id: string; name: string; slug: string }[]> {
    return listItems(await this.ctx.raw({ httpMethod: 'GET', path: '/communities/my' }))
  }

  /** A community by its slug (public route), to pick one for its overrides. */
  async communityBySlug(slug: string): Promise<{ id: string; name: string; slug: string }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/communities/slug/{slug}' }, { slug })
  }

  /** F-12: payouts of every community, newest first, with `providerData`. */
  async listPayouts(params: { communityId?: string; status?: string; from?: string; to?: string; page?: number; limit?: number }): Promise<{
    items: AdminPayout[]
    pagination?: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/admin/payouts' }, { query: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')) })
    const pagination = (result as { pagination?: { page: number; limit: number; total: number; totalPages: number } } | null)?.pagination
    return { items: listItems<AdminPayout>(result), pagination }
  }

  /** F-12: a FAILED payout sent again as a new payout (`metadata.retryOf`). 409 when not FAILED or already retried. */
  async retryPayout(payoutId: string): Promise<AdminPayout> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/payouts/{payoutId}/retry', hasBody: true }, { payoutId, body: {} })
  }

  /** F-55: refunds of every community, oldest first; `status=NEEDS_REVIEW` is the review queue. */
  async listRefunds(params: { status?: string; communityId?: string; from?: string; to?: string; page?: number; limit?: number }): Promise<{
    items: AdminRefund[]
    pagination?: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/admin/refunds' }, { query: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')) })
    const pagination = (result as { pagination?: { page: number; limit: number; total: number; totalPages: number } } | null)?.pagination
    return { items: listItems<AdminRefund>(result), pagination }
  }

  /**
   * F-55: closes a NEEDS_REVIEW refund. SUCCEEDED records it (ledger, access,
   * e-mail); DECLINED makes the amount refundable again. 409 when it is not
   * (or no longer) in review; 400 without a note.
   */
  async resolveRefund(refundId: string, body: { outcome: ReviewOutcome; note: string }): Promise<AdminRefund> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/refunds/{refundId}/resolve', hasBody: true }, { refundId, body })
  }

  /** F-12: the community's receiving account balance, what it could pay out and the payout mode. 404 without a recipient. */
  async recipientBalance(communityId: string): Promise<AdminRecipientBalance> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/payout/pagarme/balance' }, { query: { communityId } })
  }
}
